import React, { useState, useEffect } from 'react';
import { Page, Section, PageEditorProps } from './types';
import { getSchema, SectionSchema } from './schemaService';
import DynamicSectionEditor from './dynamicSectionEditor';
import SectionSchemaChooserModal from './sectionSchemaTypeChooserModal';
import { useNavigate } from 'react-router-dom';

const createInitialSectionData = (sectionType: string, sectionsSchema: SectionSchema[]): Section['data'] => {
  const sectionSchema = sectionsSchema.find(schema => schema.sectionRefs === sectionType);
  
  if (!sectionSchema) {
    return {};
  }

  const initialData: Section['data'] = {};

  Object.entries(sectionSchema.fields).forEach(([key, fieldDef]) => {
    if (fieldDef.type === 'text' || fieldDef.type === 'longtext' || 
        fieldDef.type === 'url' || fieldDef.type === 'image_url') {
      initialData[key] = '';
    } else if (fieldDef.type === 'list') {
      initialData[key] = [];
    } else if (fieldDef.type === 'block' && fieldDef.fields) {
      const obj: Record<string, any> = {};
      Object.keys(fieldDef.fields).forEach(propKey => {
        obj[propKey] = '';
      });
      initialData[key] = obj;
    }
  });

  return initialData;
};

const PageEditor: React.FC<PageEditorProps> = ({ onSubmit, loading, error, initialPage, isEditMode = false }) => {
  const [page, setPage] = useState<Page>(initialPage || {
    title: '',
    sections: []
  });
  const [activeSectionIndex, setActiveSectionIndex] = useState<number | null>(null);
  const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
  const [sectionsSchema, setSectionsSchema] = useState<SectionSchema[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const loadSchema = async () => {
      const schema = await getSchema();
      setSectionsSchema(schema);
    };
    loadSchema();
  }, []);

  useEffect(() => {
    if (initialPage) {
      setPage(initialPage);
    }
  }, [initialPage]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage({ ...page, title: e.target.value });
  };

  const addSection = (type: string) => {
    const newSection: Section = { 
      sectionRefs: type, 
      data: createInitialSectionData(type, sectionsSchema) 
    };
    
    setPage({
      ...page,
      sections: [...page.sections, newSection]
    });
    setActiveSectionIndex(page.sections.length);
    setIsSectionModalOpen(false);
  };

  const removeSection = (index: number) => {
    const newSections = [...page.sections];
    newSections.splice(index, 1);
    setPage({ ...page, sections: newSections });
    if (activeSectionIndex === index) {
      setActiveSectionIndex(null);
    } else if (activeSectionIndex !== null && activeSectionIndex > index) {
      setActiveSectionIndex(activeSectionIndex - 1);
    }
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === page.sections.length - 1)
    ) {
      return;
    }

    const newSections = [...page.sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setPage({ ...page, sections: newSections });
    setActiveSectionIndex(newIndex);
  };

  const updateSectionData = (index: number, data: Section['data']) => {
    const newSections = [...page.sections];
    newSections[index].data = data;
    setPage({ ...page, sections: newSections });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(page);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Page Title */}
        <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
          <label htmlFor="page-title" className="block text-sm font-semibold text-[#141F28] mb-2">
            Page Title
          </label>
          <input
            id="page-title"
            type="text"
            value={page.title}
            onChange={handleTitleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#277200] focus:border-[#277200] transition-all duration-300 ease-in-out"
            required
            placeholder="Enter page title"
          />
        </div>

        {/* Sections */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-[#141F28]">Page Sections</h2>

          {page.sections.length === 0 && (
            <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100 text-center">
              <p className="text-gray-500 font-medium">No sections added yet. Click "Add Section" to get started.</p>
            </div>
          )}

          {page.sections.map((section, index) => {
            const sectionSchema = sectionsSchema.find(s => s.sectionRefs === section.sectionRefs);
            
            return (
              <div 
                key={index} 
                className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden transition-all duration-300 ease-in-out"
              >
                <div 
                  className={`flex justify-between items-center p-4 cursor-pointer ${activeSectionIndex === index ? 'bg-[#277200] bg-opacity-10' : 'hover:bg-gray-50'}`}
                  onClick={() => setActiveSectionIndex(activeSectionIndex === index ? null : index)}
                >
                  <div className="flex items-center">
                    <span className="bg-[#277200] bg-opacity-20 text-[#277200] text-xs font-semibold px-2.5 py-0.5 rounded mr-3">
                      {sectionSchema?.label || section.sectionRefs}
                    </span>
                    <h3 className="font-semibold text-[#141F28]">
                      {section.data.title || 
                       (section.sectionRefs === 'slider' ? `Slider (${section.data.slides?.length || 0} slides)` : 
                        `Untitled ${section.sectionRefs} section`)}
                    </h3>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); moveSection(index, 'up'); }}
                      disabled={index === 0}
                      className={`p-1 rounded-md ${index === 0 ? 'text-gray-300' : 'text-[#141F28] hover:bg-gray-100'}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); moveSection(index, 'down'); }}
                      disabled={index === page.sections.length - 1}
                      className={`p-1 rounded-md ${index === page.sections.length - 1 ? 'text-gray-300' : 'text-[#141F28] hover:bg-gray-100'}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeSection(index); }}
                      className="p-1 rounded-md text-red-500 hover:bg-red-50"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>

                {activeSectionIndex === index && sectionSchema && (
                  <div className="p-6 border-t border-gray-100">
                    <DynamicSectionEditor 
                      schema={sectionSchema}
                      data={section.data}
                      onChange={(data) => updateSectionData(index, data)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add Section Button */}
        <div className="flex justify-center mt-6">
          <button
            type="button"
            onClick={() => setIsSectionModalOpen(true)}
            className="inline-flex items-center px-6 py-3 border-2 border-[#277200] text-base font-semibold rounded-full text-[#277200] hover:bg-[#277200] hover:text-white transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#277200] focus:ring-offset-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Section
          </button>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/pages')}
            className="px-4 py-2 border border-[#277200] text-[#277200] rounded-md shadow-sm text-sm font-semibold bg-white hover:bg-[#277200] hover:text-white transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#277200]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white bg-[#277200] hover:bg-[#1a4d00] focus:outline-none focus:ring-2 focus:ring-[#277200] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 ease-in-out"
          >
            {loading ? 'Saving...' : isEditMode ? 'Update Page' : 'Create Page'}
          </button>
        </div>

        {error && (
          <div className="p-4 bg-red-50 rounded-md border border-red-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>

      <SectionSchemaChooserModal
        isOpen={isSectionModalOpen}
        onClose={() => setIsSectionModalOpen(false)}
        onSelect={addSection}
        sectionsSchema={sectionsSchema}
      />
    </div>
  );
};

export default PageEditor;